import { useTheme } from "../../../contexts/ThemeContext";

const industries = [
  {
    icon: "ri-home-gear-line", iconColor: "#F97316", name: "Home Services",
    tagline: "Never miss a job while on-site.",
    desc: "When your crew is working, leads don't wait. Your AI handles every incoming call and books the next job automatically.",
    image: "https://public.readdy.ai/ai/img_res/9eed461979d9583fb6a61bd17e8d16e0.jpg",
    examples: ["HVAC & Plumbing", "Electrical", "Roofing & Restoration", "Landscaping", "Cleaning"],
  },
  {
    icon: "ri-heart-pulse-line", iconColor: "#22C55E", name: "Gyms & Fitness",
    tagline: "Turn every inquiry into a member.",
    desc: "Fill trial slots and class bookings automatically. Re-engage lapsed members without a single staff call.",
    image: "https://readdy.ai/api/search-image?query=premium%20boutique%20gym%20fitness%20studio%20interior%20dramatic%20lighting%20modern%20equipment%20dark%20aesthetic%20dynamic%20athlete%20training%20motivational%20powerful%20composition%20wide%20angle&width=800&height=1000&seq=ind-gym-v2&orientation=portrait",
    examples: ["Boutique Studios", "CrossFit & Functional", "Personal Training", "Yoga & Pilates", "MMA & Boxing"],
  },
  {
    icon: "ri-mental-health-line", iconColor: "#38BDF8", name: "Wellness & Spas",
    tagline: "Premium experience from first contact.",
    desc: "Every inquiry handled with care, every appointment booked with precision — before your staff even sees the message.",
    image: "https://readdy.ai/api/search-image?query=luxury%20wellness%20spa%20interior%20serene%20calm%20candlelit%20atmosphere%20minimalist%20design%20zen%20peaceful%20retreat%20high-end%20premium%20experience%20warm%20light%20soft%20shadows&width=800&height=1000&seq=ind-spa-v2&orientation=portrait",
    examples: ["Day & Med Spas", "Massage Therapy", "Acupuncture", "Holistic Health", "Float Therapy"],
  },
  {
    icon: "ri-stethoscope-line", iconColor: "#F97316", name: "Chiropractic & PT",
    tagline: "Patients in pain can't wait.",
    desc: "Capture every new patient inquiry and get them booked 24/7 — no front desk delays, no missed opportunities.",
    image: "https://readdy.ai/api/search-image?query=modern%20chiropractic%20clinic%20medical%20office%20clean%20professional%20bright%20interior%20minimalist%20design%20consultation%20room%20premium%20healthcare%20facility%20natural%20light&width=800&height=1000&seq=ind-chiro-v2&orientation=portrait",
    examples: ["Chiropractic Clinics", "Physical Therapy", "Sports Medicine", "Orthopedics", "Pain Management"],
  },
];

export default function IndustriesServed() {
  const { isDark } = useTheme();

  const sectionBg = isDark ? "bg-black" : "bg-white";
  const titleColor = isDark ? "text-white" : "text-[#1d1d1f]";
  const subColor = isDark ? "text-white/45" : "text-[#6e6e73]";
  const calloutBg = isDark ? "bg-[#0A0A10] border-white/[0.07]" : "bg-[#F5F5F7] border-black/[0.07]";
  const calloutTitle = isDark ? "text-white" : "text-[#1d1d1f]";
  const calloutSub = isDark ? "text-white/45" : "text-[#6e6e73]";

  return (
    <section id="industries" className={`${sectionBg} py-24 md:py-36 px-6 md:px-10 transition-colors duration-300`}>
      <div className="max-w-[1280px] mx-auto">

        <div className="text-center mb-16">
          <p className="text-[#38BDF8] text-[11px] font-semibold tracking-[0.2em] uppercase mb-5">Industries Served</p>
          <h2 className={`text-[42px] md:text-[60px] font-black ${titleColor} tracking-[-0.03em] leading-tight mb-5`}>
            Built for businesses<br />that run on speed.
          </h2>
          <p className={`${subColor} text-[17px] max-w-lg mx-auto leading-relaxed`}>
            Specifically engineered for industries where the first responder wins — and slow follow-up means lost revenue.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {industries.map((ind, i) => (
            <div key={i} className="group relative rounded-[24px] overflow-hidden cursor-default" style={{ minHeight: "480px" }}>
              <img
                src={ind.image}
                alt={ind.name}
                className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/65 to-black/10"></div>
              <div className="absolute inset-0 flex flex-col justify-end p-6">
                <div className="w-9 h-9 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-xl border border-white/15 mb-4">
                  <i className={`${ind.icon} text-sm`} style={{ color: ind.iconColor }}></i>
                </div>
                <p className="text-[10px] font-semibold tracking-widest uppercase mb-1" style={{ color: ind.iconColor }}>{ind.tagline}</p>
                <h3 className="text-white font-black text-[22px] tracking-tight mb-2">{ind.name}</h3>
                <p className="text-white/55 text-[13px] leading-relaxed mb-4">{ind.desc}</p>
                <div className="flex flex-wrap gap-1.5">
                  {ind.examples.map((ex, j) => (
                    <span key={j} className="text-white/50 text-[10px] font-medium border border-white/12 px-2 py-0.5 rounded-full">{ex}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={`mt-10 rounded-[24px] border ${calloutBg} px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-5`}>
          <div>
            <p className={`${calloutTitle} font-semibold text-[16px]`}>Don&apos;t see your industry?</p>
            <p className={`${calloutSub} text-[14px] mt-0.5`}>If your business runs on calls, appointments, and fast response — AI Talent Launch was built for you.</p>
          </div>
          <button
            onClick={() => { const el = document.getElementById("contact"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}
            className="bg-[#F97316] text-white text-[13px] font-semibold px-6 py-2.5 rounded-full hover:bg-[#FB923C] hover:scale-[1.02] transition-all cursor-pointer whitespace-nowrap shrink-0"
          >
            Let&apos;s Talk
          </button>
        </div>
      </div>
    </section>
  );
}
